import { Gondola, useGondolasQuery, useLazyGondolasQuery } from "@/shared/api";
import {
  Box,
  Button,
  ChevronDownIcon,
  Flex,
  Popover,
  ScrollArea,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Command } from "cmdk";
import { useHotkeys } from "react-hotkeys-hook";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  selectIsSkuSectionPlanogramVisible,
  selectPlanogramViewGondola,
  setGondolas,
} from "@/entities/annotation";
import { CAMERA_MODES } from "@/entities/annotation/model/consts";
import { getImageSrc } from "@/shared/api/helpers";

type GondolaSelectProps = {
  value: Gondola | undefined | null;
  disabled?: boolean;
  onValueChange?: (value: Gondola | null) => void;
};

export const allOption: Gondola = {
  id: -1,
  name: CAMERA_MODES.ALL.charAt(0).toUpperCase() + CAMERA_MODES.ALL.slice(1),
  cameras: [],
  shelfTrackingCameras: [],
  handTrackingCameras: [],
};

export function GondolaSelect({
  value,
  disabled = false,
  onValueChange,
}: GondolaSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const isPlanogramViewVisible = useAppSelector(
    selectIsSkuSectionPlanogramVisible
  );
  const planogramViewGondola = useAppSelector(selectPlanogramViewGondola);
  const [getGondolas] = useLazyGondolasQuery();
  const dispatch = useAppDispatch();

  const selectedOrder = useAppSelector(
    (state) => state.annotation.selectedOrder
  );

  const { data } = useGondolasQuery(
    {
      search,
      on_datetime: selectedOrder?.time,
    },
    {
      skip: !selectedOrder,
    }
  );

  useEffect(() => {
    if (selectedOrder) {
      getGondolas({
        on_datetime: selectedOrder.time,
      }).then((result) => {
        if (result.data?.results) {
          dispatch(setGondolas(result.data?.results));
        }
      });
    }
  }, [selectedOrder]);

  useHotkeys("ctrl+f", () => setOpen(true), { preventDefault: true });

  const handleSelect = (gondola: Gondola) => () => {
    onValueChange?.(gondola);
    setOpen(false);
    setSearch("");
  };

  const displayValue = () => {
    if (isPlanogramViewVisible && planogramViewGondola) {
      return planogramViewGondola.name;
    }

    return value ? value.name : "Select gondola";
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button
          variant="outline"
          size="1"
          color="gray"
          className="min-w-32 justify-between"
          disabled={disabled}
        >
          {displayValue()} <ChevronDownIcon />
        </Button>
      </Popover.Trigger>
      <Popover.Content width="250px" className="p-0" align="center">
        <Command shouldFilter={false}>
          <Command.Input asChild>
            <TextField.Root
              value={search}
              onChange={(e) => {
                e.stopPropagation();
                setSearch(e.target.value);
              }}
              size="1"
              m="2"
              placeholder="Search"
            ></TextField.Root>
          </Command.Input>

          <ScrollArea className="max-h-[400px] pl-2 pr-3 pb-2">
            <Command.List className="space-y-2">
              {!isPlanogramViewVisible && (
                <Command.Item
                  value={allOption.name}
                  onSelect={handleSelect(allOption)}
                  asChild
                >
                  <Flex
                    gap="2"
                    className={twMerge([
                      "mb-1 last:mb-0 outline-none hover:bg-gray-100 rounded-lg data-[selected='true']:bg-gray-100",
                      value?.id === allOption.id &&
                        "bg-indigo-100 data-[selected='true']:bg-indigo-200",
                    ])}
                  >
                    <Box className="size-16" />
                    <Flex direction="column" justify="center">
                      <Text size="2">{allOption.name}</Text>
                    </Flex>
                  </Flex>
                </Command.Item>
              )}

              {data?.results.length === 0 ? (
                <Command.Empty>No results found.</Command.Empty>
              ) : (
                data?.results?.map((gondola) => (
                  <Command.Item
                    key={gondola.id}
                    value={String(gondola.id)}
                    onSelect={handleSelect(gondola)}
                    asChild
                  >
                    <Flex
                      gap="2"
                      className={twMerge([
                        "mb-1 last:mb-0 outline-none hover:bg-gray-100 rounded-lg data-[selected='true']:bg-gray-100",
                        value?.id === gondola.id &&
                          "bg-indigo-100 data-[selected='true']:bg-indigo-200",
                      ])}
                    >
                      <figure className="size-16 rounded bg-gray-200">
                        <img
                          src={getImageSrc(
                            gondola.shelfTrackingCameras?.[0]?.thumbnail
                          )}
                          className="object-contain w-full h-full"
                        />
                      </figure>
                      <Flex direction="column" justify="center">
                        <Text size="2">{gondola.name}</Text>
                      </Flex>
                    </Flex>
                  </Command.Item>
                ))
              )}
            </Command.List>
          </ScrollArea>
        </Command>
      </Popover.Content>
    </Popover.Root>
  );
}
